/**
 * <search-panel> — drop-down search with predictive results.
 *
 * Results are rendered by the `predictive-search` section on Shopify's side and
 * swapped in as HTML, so no templating happens here and the markup matches the rest
 * of the theme.
 *
 * Requests are debounced and each one aborts the previous, which prevents a slow
 * early response from overwriting the results of a later keystroke.
 */
/* Wrapped in an IIFE so its top-level names stay out of the global scope.
   Theme scripts share one global namespace: when two of them declared the same
   const, the second failed to parse outright and silently did nothing. */
(() => {
  const DEBOUNCE_MS = 250;
  const MIN_QUERY = 2;

  class SearchPanel extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-search-input]');
      this.mount = this.querySelector('[data-predictive-mount]');
      this.timer = null;
      this.controller = null;

      this.querySelectorAll('[data-search-close]').forEach((el) =>
        el.addEventListener('click', () => this.close())
      );

      this.input?.addEventListener('input', this.#onInput);
      document.addEventListener('keydown', this.#onKeydown);

      // Clicking a suggested term searches it in place rather than navigating away.
      this.addEventListener('click', (event) => {
        const term = event.target.closest('[data-search-term]');
        if (!term || !this.input) return;
        event.preventDefault();
        this.input.value = term.dataset.searchTerm;
        this.input.focus();
        this.#search(this.input.value);
      });
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.#onKeydown);
    }

    open() {
      // The panels hang off the same header edge and would otherwise stack.
      ['menu-panel', 'cart-drawer'].forEach((id) =>
        document.getElementById(id)?.close?.()
      );

      this.opener = document.activeElement;
      this.setAttribute('data-open', '');
      document.documentElement.style.overflow = 'hidden';
      document.documentElement.style.scrollbarGutter = 'stable';
      // Wait a frame so the field is focusable before focus is moved to it.
      requestAnimationFrame(() => this.input?.focus());
    }

    close() {
      this.removeAttribute('data-open');
      document.documentElement.style.overflow = '';
      document.documentElement.style.scrollbarGutter = '';
      this.opener?.focus();
    }

    get isOpen() {
      return this.hasAttribute('data-open');
    }

    #onKeydown = (event) => {
      if (event.key === 'Escape' && this.isOpen) this.close();
    };

    #onInput = () => {
      const query = this.input.value.trim();
      clearTimeout(this.timer);

      if (query.length < MIN_QUERY) {
        this.removeAttribute('data-searching');
        this.mount.innerHTML = '';
        return;
      }

      this.setAttribute('data-searching', '');
      this.timer = setTimeout(() => this.#search(query), DEBOUNCE_MS);
    };

    async #search(query) {
      this.controller?.abort();
      this.controller = new AbortController();

      const url = new URL(`${window.Shopify.routes.root}search/suggest`, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('section_id', 'predictive-search');
      url.searchParams.set('resources[type]', 'product,article,page');
      url.searchParams.set('resources[limit]', '6');

      try {
        const response = await fetch(url, { signal: this.controller.signal });
        const html = await response.text();
        const results = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('[data-predictive-results]');
        this.mount.replaceChildren(...(results ? [results] : []));
      } catch (error) {
        if (error.name !== 'AbortError') console.error('[search]', error);
      }
    }
  }

  if (!customElements.get('search-panel')) {
    customElements.define('search-panel', SearchPanel);
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-search-open]');
    if (!trigger) return;
    const panel = document.getElementById('search-panel');
    if (!(panel instanceof SearchPanel)) return;
    event.preventDefault();
    panel.isOpen ? panel.close() : panel.open();
  });
})();
